import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ItemService } from '../item.service';
import { SuppliersService } from '../../suppliers/suppliers.service';
import { SupplierSiteService } from '../../suppliers/supplierSite/supplierSite.service';
import { BusinessService } from '../../business/business.service';
import { Item } from '../item.model';
import { CardModule } from 'primeng/card';
import { Business } from '../../business/business.model';
import { BrandsService } from '../../brands/brands.service';
import { Brand } from '../../brands/brands.model';
import { SubBrandsService } from '../../brands/sub-brands/sub-brands.service';
import { SubBrand } from '../../brands/sub-brands/sub-brands.model';
import { ItemCategoryService } from '../item-category.service';
import { ItemSubCategoryService } from '../item-sub-category.service';
import { ItemCategory, ItemSubCategory } from '../item.model';
import { Supplier } from '../../suppliers/suppliers.model';
import { SupplierSite } from '../../suppliers/supplierSite/supplierSite.model';
import { EntityDropdownPickerComponent } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { IftaLabelModule } from 'primeng/iftalabel';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-edit-item',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    CardModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    EntityDropdownPickerComponent,
  ],
  templateUrl: './item-form.component.html',
  styleUrl: './item-form.component.css',
  providers: [MessageService],
})
export class ItemFormComponent implements OnInit, OnDestroy {
  itemId: number | null = null;
  itemForm!: FormGroup;
  isEditMode = false;
  pageTitle = 'Create New Item';
  brandPickerAdapter!: EntityDropdownPickerAdapter<Brand>;
  subBrandPickerAdapter!: EntityDropdownPickerAdapter<SubBrand>;
  categoryPickerAdapter!: EntityDropdownPickerAdapter<ItemCategory>;
  subCategoryPickerAdapter!: EntityDropdownPickerAdapter<ItemSubCategory>;
  businessPickerAdapter!: EntityDropdownPickerAdapter<Business>;
  supplierPickerAdapter!: EntityDropdownPickerAdapter<Supplier>;
  supplierSitePickerAdapter!: EntityDropdownPickerAdapter<SupplierSite>;
  private categorySub?: Subscription;
  private isPatchingItem = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private ItemService: ItemService,
    private messageService: MessageService,
    private BrandsService: BrandsService,
    private SubBrandsService: SubBrandsService,
    private ItemCategoryService: ItemCategoryService,
    private ItemSubCategoryService: ItemSubCategoryService,
    private SuppliersService: SuppliersService,
    private SupplierSiteService: SupplierSiteService,
    private BusinessService: BusinessService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.brandPickerAdapter = this.BrandsService.createBrandPickerAdapter();
    this.subBrandPickerAdapter = this.SubBrandsService.createSubBrandPickerAdapter();
    this.categoryPickerAdapter = this.ItemCategoryService.createItemCategoryPickerAdapter();
    this.subCategoryPickerAdapter = this.ItemSubCategoryService.createItemSubCategoryPickerAdapter(null);
    this.supplierPickerAdapter = this.SuppliersService.createSupplierPickerAdapter();
    this.supplierSitePickerAdapter = this.SupplierSiteService.createSupplierSitePickerAdapter();
    this.businessPickerAdapter = this.BusinessService.createBusinessPickerAdapter();
    this.categorySub = this.itemForm.get('category')?.valueChanges.subscribe((category: ItemCategory | null) => {
      if (this.isPatchingItem) {
        return;
      }

      this.subCategoryPickerAdapter = this.ItemSubCategoryService.createItemSubCategoryPickerAdapter(category?.id ?? null);
      this.itemForm.patchValue({ subCategory: null }, { emitEvent: false });
    });
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.itemId = +id;
        this.isEditMode = true;
        this.pageTitle = 'Edit Item';
        this.loadItemData(this.itemId);
      }
    });
  }

  initForm(): void {
    this.itemForm = this.fb.group({
      itemId: [''],
      erpItemId: [''],
      erpItemNumber: [''],
      erpItemNameA: [''],
      erpItemNameE: [''],
      erpItemUom: [''],
      erpVendorCatalog: [''],
      erpItemStatus: [''],
      brand: [null],
      subBrand: [null],
      category: [null],
      subCategory: [null],
      supplier: [null],
      supplierSite: [null],
      business: [null],
    });
  }

  loadItemData(id: number): void {
    this.ItemService.getItemById(id).subscribe({
      next: (item) => {
        this.isPatchingItem = true;
        this.itemForm.patchValue(
          {
            ...item,
            category: item.category ?? null,
            subCategory: item.subCategory ?? null,
            brand: item.brand ?? null,
            subBrand: item.subBrand ?? null,
            supplier: item.supplier ?? null,
            supplierSite: item.supplierSite ?? null,
            business: item.business ?? null,
          },
          { emitEvent: false }
        );
        this.subCategoryPickerAdapter = this.ItemSubCategoryService.createItemSubCategoryPickerAdapter(item.category?.id ?? null);
        this.isPatchingItem = false;
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load item data.',
        });
        console.error(err);
      },
    });
  }

  saveItem(): void {
    if (this.itemForm.invalid) {
      this.itemForm.markAllAsTouched();
      return;
    }

    const itemData: Item = {
      itemId: this.itemId ?? 0,
      ...this.itemForm.value,
      brand: this.itemForm.value.brand,
      subBrand: this.itemForm.value.subBrand,
      supplier: this.itemForm.value.supplier,
      supplierSite: this.itemForm.value.supplierSite,
      business: this.itemForm.value.business,
    };
    const operation = this.isEditMode
      ? this.ItemService.updateItem(itemData)
      : this.ItemService.createItem(itemData);

    operation.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Item ${
            this.isEditMode ? 'updated' : 'created'
          } successfully.`,
        });
        setTimeout(
          () =>
            this.router.navigate([
              '/ai-studio/session/current',
              { outlets: { canvasOutlet: ['items', 'list'] } },
            ]),
          1500
        );
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${this.isEditMode ? 'update' : 'create'} item.`,
        });
        console.error(err);
      },
    });
  }

  cancel(): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['items', 'list'] } },
    ]);
  }

  ngOnDestroy(): void {
    this.categorySub?.unsubscribe();
  }
}
